/**
 * G11 / V31: stated routing preferences (rules per task type) and the learned-outcome history
 * the router weighs against them. Both are content-derived (they reveal what the user works on),
 * so they live only as encrypted `setting` entities (`routingPreferences`, `routingOutcomes`):
 * the hub never sees them. Versioned; additive only.
 */
import { z } from 'zod';
const Ts = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const ROUTING_TASK_TYPES = [
    'image-generation',
    'gpu-windows',
    'goal-loop',
    'building',
    'bugfix',
    'refactor',
    'testing',
    'docs',
    'research',
    'review',
    'general',
];
export const RoutingTaskType = z.enum(ROUTING_TASK_TYPES);
export const ROUTING_TASK_TYPE_LABEL = {
    'image-generation': 'image generation',
    'gpu-windows': 'Windows / GPU',
    'goal-loop': 'long goal loops',
    building: 'building',
    bugfix: 'bug fixes',
    refactor: 'refactoring',
    testing: 'testing',
    docs: 'docs',
    research: 'research',
    review: 'review',
    general: 'general',
};
/** One stated preference: for this kind of task, prefer this agent and/or machine. */
export const RoutingRule = z
    .strictObject({
    id: z.string().min(1).max(64),
    taskType: RoutingTaskType,
    agent: z.enum(['claude', 'codex']).optional(),
    /** Machine id (or its name); a preference, never a way around load or capability limits. */
    machine: z.string().min(1).max(120).optional(),
    /** Only for this project (absent = every project). */
    projectId: z.string().min(1).max(120).optional(),
})
    .refine((r) => r.agent !== undefined || r.machine !== undefined, {
    message: 'A rule needs an agent or a machine',
});
export const RoutingPreferences = z.strictObject({
    version: z.literal(1),
    rules: z.array(RoutingRule).max(100),
    /** Learn from verified outcomes when no rule decides. */
    learn: z.boolean(),
    /** Similar tasks needed per candidate before learned evidence may decide. */
    minSamples: z.number().int().min(1).max(100),
    updatedAt: Ts,
});
export const DEFAULT_ROUTING_PREFERENCES = {
    version: 1,
    rules: [],
    learn: true,
    minSamples: 5,
    updatedAt: 0,
};
/** One finished run, reduced to what routing learns from. Verified = landed/green, not claimed. */
export const RoutingOutcome = z.strictObject({
    taskType: RoutingTaskType,
    agent: z.enum(['claude', 'codex']),
    model: z.string().min(1).max(120).optional(),
    machineId: z.string().min(1).max(120).optional(),
    accountId: z.string().min(1).max(160).optional(),
    verified: z.boolean(),
    /** Project and task the run belonged to (G18: per-project model evidence, escalation). */
    projectId: z.string().min(1).max(128).optional(),
    taskId: z.string().min(1).max(128).optional(),
    /** "I told you before" style corrections the user had to make. */
    corrections: z.number().int().nonnegative().max(1000).optional(),
    durationMs: z.number().int().nonnegative().optional(),
    costUsd: z.number().nonnegative().optional(),
    at: Ts,
});
export const RoutingOutcomes = z.strictObject({
    version: z.literal(1),
    outcomes: z.array(RoutingOutcome).max(2000),
    updatedAt: Ts,
});
/** Keep the newest `max` outcomes (the store is bounded; old evidence also decays in scoring). */
export function appendOutcome(store, o, now, max = 2000) {
    const all = [...(store?.outcomes ?? []), o].sort((a, b) => a.at - b.at);
    return { version: 1, outcomes: all.slice(-max), updatedAt: now };
}
// ---- classifier (shared by scheduler, daemon and browser) ------------------------------------
const KEYWORDS = [
    ['image-generation', /\b(image|icon|sprite|illustration|logo|artwork|texture|thumbnail)s?\b/i],
    [
        'goal-loop',
        /\b(goal[- ]loop|until (it )?(passes|green|done)|keep (going|iterating)|overnight)\b/i,
    ],
    ['gpu-windows', /\b(gpu|cuda|shader|unity|unreal|directx|windows|powershell|\.exe|\.csproj)\b/i],
    ['bugfix', /\b(fix|bug|crash|regression|broken|error|fails?|failing|hotfix)\b/i],
    ['refactor', /\b(refactor|rename|cleanup|clean up|restructure|migrate|simplif(y|ication))\b/i],
    ['testing', /\b(tests?|coverage|e2e|vitest|jest|playwright|flaky)\b/i],
    ['docs', /\b(docs?|readme|documentation|changelog|comment(s)?)\b/i],
    ['research', /\b(research|investigate|compare|survey|spike|explore|evaluate)\b/i],
    ['review', /\b(review|audit|critique|verify)\b/i],
    ['building', /\b(build|implement|add|create|feature|ship|develop|integrate|support)\b/i],
];
/** Deterministic heuristics: labels first (hard signals), then title keywords, then general. */
export function classifyTaskType(title, labels = []) {
    if (labels.includes('image-gen'))
        return 'image-generation';
    if (labels.includes('goal-loop'))
        return 'goal-loop';
    if (labels.includes('needs-gpu') || labels.includes('needs-windows'))
        return 'gpu-windows';
    for (const [type, re] of KEYWORDS)
        if (re.test(title ?? ''))
            return type;
    return 'general';
}
// ---- per-run entities ----------------------------------------------------------------------------
// Each finished run is one small encrypted `setting` entity (name `routingOutcome`, written by the
// daemon that observed the landing/usage) and user corrections another (`routingCorrections`,
// written by the browser), so the two writers never contend for one document.
export const RoutingRunOutcome = RoutingOutcome.extend({ runId: z.string().min(1).max(128) });
export const RoutingCorrections = z.strictObject({
    runId: z.string().min(1).max(128),
    count: z.number().int().min(1).max(1000),
    at: Ts,
});
/** Heuristic for "the user is redirecting, undoing or fixing up": a correction, not a new ask. */
export function isCorrectionMessage(text) {
    return /\b(undo|revert|roll ?back|not what i (asked|meant|wanted)|that'?s (wrong|not right)|wrong (file|approach|way)|i told you|i said|instead[, ]|actually[, ]|no[, ]+(stop|don'?t|that)|don'?t do that|try again|redo|fix (that|it|this) up|you (broke|missed|forgot))\b/i.test(text.slice(0, 2000));
}
/**
 * Content-free routing hint an endpoint attaches to a task so the hub scheduler (which cannot read
 * preferences or history) can honor them: an agent order and/or machine, with the source.
 */
export const RoutingHint = z.strictObject({
    agent: z.enum(['claude', 'codex']).optional(),
    machine: z.string().min(1).max(120).optional(),
    source: z.enum(['rule', 'learned']),
    at: Ts,
});
//# sourceMappingURL=routing-prefs.js.map